import React, { useState, useRef, useEffect } from "react";
import "../styles/Chatty.css";
import { useNavigate } from "react-router-dom";

const WS_URL = "ws://193.123.94.90:8000/ws";
const API_URL = "http://193.123.94.90:8000/get-user-info";

export default function Chat() {
  const navigate = useNavigate();

  const [username, setUsername] = useState("");
  const [role, setRole] = useState("");
  const [connected, setConnected] = useState(false);
  const ws = useRef(null);

  const [convos, setConvos] = useState({});
  const [currentPeer, setCurrentPeer] = useState("");
  const [msg, setMsg] = useState("");
  const [availableTeachers, setAvailableTeachers] = useState([]);
  const [teacherSearch, setTeacherSearch] = useState("");
  const [toast, setToast] = useState({ message: "", show: false });

  const logRef = useRef(null);

  const showToast = (message) => {
    setToast({ message, show: true });
    setTimeout(() => setToast({ message: "", show: false }), 3000);
  };

  const isJSON = (str) => {
    try {
      JSON.parse(str);
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    logRef.current?.scrollTo({ top: logRef.current.scrollHeight, behavior: "smooth" });
  }, [convos, currentPeer]);

  useEffect(() => {
    const fetchUserInfo = async () => {
      const token = localStorage.getItem("token");
      if (!token) return navigate("/customError");

      try {
        const res = await fetch(API_URL, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) throw new Error("Failed to fetch user info");

        const data = await res.json();
        setUsername(data.name);
        setRole(data.role);

        if (data.role === "student") {
          setAvailableTeachers(data.names || []);
        }

        const initialConvos = {};
        (data.names || []).forEach((name) => {
          initialConvos[name] = [];
        });
        setConvos(initialConvos);
      } catch (err) {
        console.error("User info fetch error:", err);
        showToast("⚠️ Error fetching user info. Please log in again.");
        navigate("/");
      }
    };

    fetchUserInfo();
  }, [navigate]);

  useEffect(() => {
    if (!username || !role) return;

    ws.current = new WebSocket(`${WS_URL}?user=${username}&role=${role}`);

    ws.current.onopen = () => setConnected(true);

    ws.current.onmessage = (e) => {
      if (!isJSON(e.data)) {
        showToast("User Not Connected❗");
        return;
      }

      try {
        const data = JSON.parse(e.data);
        if (data.error) {
          showToast("⚠️ " + data.error);
          return;
        }

        const peer = data.from === username ? data.to : data.from;
        setConvos((prev) => {
          const updated = prev[peer] ? [...prev[peer], data] : [data];
          return { ...prev, [peer]: updated };
        });

        if (!currentPeer) setCurrentPeer(peer);
      } catch (err) {
        console.error("Message parse error", err);
      }
    };

    ws.current.onclose = () => setConnected(false);

    return () => ws.current?.close();
  }, [username, role]);

  const send = () => {
    if (!currentPeer) return showToast("⚠️ Select a conversation first.");
    if (!msg.trim()) return;

    try {
      ws.current?.send(JSON.stringify({ to: currentPeer, message: msg.trim() }));
    } catch (err) {
      console.error("WebSocket send failed:", err);
      showToast("❌ Message could not be sent.");
    }

    setMsg("");
  };

  const peers = Object.keys(convos);
  const filteredTeachers = availableTeachers.filter((t) =>
    t.toLowerCase().includes(teacherSearch.toLowerCase())
  );

  return (
    <div
      className="chat-layout position-relative"
      style={{
        backgroundColor: "#ffeae9",
        minHeight: "100vh",
        cursor: 'url("https://cdn-icons-png.flaticon.com/512/60/60995.png") 0 30, auto',
      }}
    >
      {toast.show && <div className="custom-toast">{toast.message}</div>}

      <div
        style={{
          backgroundImage: 'url("/background.jpg")',
          backgroundSize: "contain",
          backgroundRepeat: "repeat",
          backgroundPosition: "center",
          opacity: 0.04,
          position: "absolute",
          inset: 0,
          zIndex: 0,
          pointerEvents: "none",
        }}
      ></div>

      <div className="position-absolute top-0 start-0 p-3" style={{ zIndex: 1000 }}>
        <button
          className="btn btn-outline-secondary btn-sm d-flex align-items-center px-2 py-1"
          style={{
            fontSize: "0.8rem",
            borderRadius: "20px",
            textDecoration: "none",
            backgroundColor: "white",
          }}
          onClick={() => {
            if (role === "student") navigate("/profile/student");
            else if (role === "teacher") navigate("/profile/teacher");
            else navigate("/");
          }}
        >
          <img
            src="/arrow_back.png"
            alt="Back"
            style={{ width: "16px", height: "16px", marginRight: "6px" }}
          />
          Back
        </button>
      </div>

      <div
        className="chat-containers"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "16px",
          padding: "1rem",
          zIndex: 1,
        }}
      >
        <aside className="chat-sidebar">
          <h3>Conversations</h3>
          {peers.length === 0 && <p className="placeholder">No chats yet</p>}
          {peers.map((peer) => (
            <div
              key={peer}
              className={`peer ${peer === currentPeer ? "active" : ""}`}
              onClick={() => setCurrentPeer(peer)}
            >
              <div className="peer-avatar">{peer.charAt(0).toUpperCase()}</div>
              <span>{peer}</span>
            </div>
          ))}

          {role === "student" && (
            <div className="available-teachers">
              <h4>Available Teachers</h4>
              <input
                className="input"
                placeholder="Search teachers..."
                value={teacherSearch}
                onChange={(e) => setTeacherSearch(e.target.value)}
              />
              <div className="teacher-list">
                {filteredTeachers.map((teacher) => (
                  <div
                    key={teacher}
                    className="teacher-option"
                    onClick={() => {
                      if (!convos[teacher]) {
                        setConvos((prev) => ({ ...prev, [teacher]: [] }));
                      }
                      setCurrentPeer(teacher);
                    }}
                  >
                    {teacher}
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        <main className="chat-main">
          <header className="chat-header">
            <h3>{currentPeer || "Select a conversation"}</h3>
            <p className="logged-in">
              Logged in as: {username || "Loading..."} ({role || "..."})
            </p>
          </header>

          <div className="chat-log" ref={logRef}>
            {currentPeer &&
              convos[currentPeer]?.map((msgObj, i) => {
                const isMe = msgObj.from === username;
                const time = new Date(msgObj.timestamp).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                });
                return (
                  <div key={i} className={`chat-bubble ${isMe ? "me" : "other"}`}>
                    <span className="bubble-meta">{isMe ? "You" : msgObj.from}</span>
                    <div className="bubble-text">{msgObj.text}</div>
                    <div className="bubble-time">{time}</div>
                  </div>
                );
              })}
          </div>

          <div className="chat-input-row">
            <input
              className="input"
              placeholder="Type your message"
              value={msg}
              onChange={(e) => setMsg(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
            />
            <button className="btn" onClick={send}>
              Send
            </button>
          </div>
        </main>
      </div>
    </div>
  );
}
